import React from 'react';
import { Button, Box } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { QuizQuestionOption } from '../../types/quiz';

interface AnswerOptionButtonProps {
  option: QuizQuestionOption;
  optionIndex: number;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  showFeedback?: boolean;
  isCorrect?: boolean;
}

// Kahoot-style colors
const OPTION_COLORS = [
  '#E21B3C', // Red
  '#1368CE', // Blue
  '#D89E00', // Yellow
  '#26890C', // Green
  '#FF8C1A', // Orange
  '#9C4CDD', // Purple
];

const AnswerOptionButton: React.FC<AnswerOptionButtonProps> = ({
  option,
  optionIndex,
  selected,
  onClick,
  disabled = false,
  showFeedback = false,
  isCorrect = false,
}) => {
  const color = OPTION_COLORS[optionIndex % OPTION_COLORS.length];

  const getBackgroundColor = () => {
    if (showFeedback) {
      if (selected && isCorrect) return '#4CAF50'; // Green for correct
      if (selected && !isCorrect) return '#F44336'; // Red for incorrect
      if (option.isCorrect) return '#4CAF50'; // Show correct answer
    }
    if (selected) return color;
    return 'white';
  };

  const getTextColor = () => {
    if (showFeedback || selected) return 'white';
    return color;
  };

  const getBorderColor = () => {
    if (showFeedback) {
      if (selected && isCorrect) return '#4CAF50';
      if (selected && !isCorrect) return '#F44336';
      if (option.isCorrect) return '#4CAF50';
    }
    return color;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.3, delay: optionIndex * 0.1 }}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      <Button
        fullWidth
        variant={selected || showFeedback ? 'contained' : 'outlined'}
        onClick={onClick}
        disabled={disabled}
        sx={{
          py: 3,
          px: 3,
          fontSize: { xs: '1rem', md: '1.25rem' },
          fontWeight: 'bold',
          borderWidth: 3,
          borderColor: getBorderColor(),
          backgroundColor: getBackgroundColor(),
          color: getTextColor(),
          textTransform: 'none',
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          '&:hover': {
            borderWidth: 3,
            borderColor: getBorderColor(),
            backgroundColor: showFeedback ? getBackgroundColor() : (selected ? color : 'white'),
            boxShadow: disabled ? 'none' : 3,
          },
          '&.Mui-disabled': {
            borderColor: getBorderColor(),
            backgroundColor: getBackgroundColor(),
            color: getTextColor(),
            opacity: 0.9,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box
            sx={{
              minWidth: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: showFeedback ? 'rgba(255,255,255,0.3)' : (selected ? 'rgba(255,255,255,0.3)' : color),
              color: showFeedback || selected ? 'white' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
            }}
          >
            {String.fromCharCode(65 + optionIndex)}
          </Box>
          <Box sx={{ flexGrow: 1, textAlign: 'left' }}>{option.optionText}</Box>
          {showFeedback && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.4 }}
            >
              <Box>
                {selected && isCorrect && <CheckCircle sx={{ fontSize: 32 }} />}
                {selected && !isCorrect && <Cancel sx={{ fontSize: 32 }} />}
                {!selected && option.isCorrect && <CheckCircle sx={{ fontSize: 32 }} />}
              </Box>
            </motion.div>
          )}
        </Box>
      </Button>
    </motion.div>
  );
};

export default AnswerOptionButton;
